import React, { useState, useEffect, useMemo } from 'react';
import { Transform, Settings } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { toast } from 'react-toastify';
import { Button, Tooltip } from '@mui/material';
import TransformationPropertiesDrawer from './CDXTransformationDrawer/TransformationPropertiesDrawer';
import {
  transformationHeaderExtendedInitialData,
  formatTransformationHeaderPayload,
  prepareTransformationHeaderFormRow,
  TRANSFORMATION_HEADER_TYPES,
  filterTransformationHeadersByType,
  filterKeyForFixedType,
  transformationHeaderFormFieldsForFixedType,
  transformationHeaderColumnsForFixedType,
} from './common/transformationHeaderUiConfig';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/transformation-header`;

/**
 * CDX / global transformation / data schema screen.
 * @param {string} fixedType - TRANSFORMATION_HEADER_TYPES.SYNC_FLOW | DATA_HUB
 * @param {string} title - Page title
 * @param {string} propertiesTitle - Drawer heading
 */
const CDXTransformation = ({
  fixedType = TRANSFORMATION_HEADER_TYPES.DATA_HUB,
  title = 'Global Data Schema',
  propertiesTitle = 'Global Data Schema Properties',
  functionCode = 'Global Data Schema',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [isPropertiesDrawerOpen, setIsPropertiesDrawerOpen] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState(null);

  const typeFilter = useMemo(() => filterKeyForFixedType(fixedType), [fixedType]);

  const filteredHeaders = useMemo(
    () => filterTransformationHeadersByType(headers, typeFilter),
    [headers, typeFilter]
  );

  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchHeaders();
  }, []);

  const fetchHeaders = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const mappedData = data.map((item) => ({
        ...item,
        id: item.id || `temp-${Math.random().toString(36).substring(2, 11)}`,
      }));

      setHeaders(mappedData);
      return mappedData;
    } catch (error) {
      console.error('Error fetching transformation headers:', error);
      toast.error(`Error fetching transformation headers: ${error.message}`);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const withFixedType = (data) => ({
    ...data,
    type: fixedType,
  });

  const handleCreate = async (data) => {
    try {
      const cleanData = formatTransformationHeaderPayload(withFixedType(data));
      const token = localStorage.getItem('token');
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(cleanData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      toast.success(`${title} created successfully`);
      await fetchHeaders();
      return responseData;
    } catch (error) {
      console.error('Create error full details:', error);
      toast.error(`Error creating ${title}: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const formattedData = formatTransformationHeaderPayload(withFixedType(data));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      toast.success(`${title} updated successfully`);
      await fetchHeaders();
      return responseData;
    } catch (error) {
      console.error('Update error full details:', error);
      toast.error(`Error updating ${title}: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Unable to delete ${title} (status ${response.status}).`
        );
      }

      toast.success(`${title} deleted successfully`);
      await fetchHeaders();
      return true;
    } catch (error) {
      console.error('Delete error full details:', error);
      throw error;
    }
  };

  const openPropertiesDrawer = (header) => {
    setSelectedHeader(header);
    setIsPropertiesDrawerOpen(true);
  };

  const columns = [
    { field: 'headerCode', headerName: 'Header Code', width: 180, flex: 0 },
    { field: 'category', headerName: 'Category', width: 120, flex: 0 },
    { field: 'subCategory', headerName: 'Sub Category', width: 120, flex: 0 },
    ...transformationHeaderColumnsForFixedType(fixedType),
    {
      field: 'targetJson',
      headerName: 'Target JSON',
      width: 300,
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) return '-';
        const jsonStr = typeof params.value === 'string' ? params.value : JSON.stringify(params.value);
        const truncated = jsonStr.length > 80 ? jsonStr.substring(0, 80) + '...' : jsonStr;
        return (
          <span title={jsonStr} style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
            {truncated}
          </span>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Properties',
      width: 100,
      flex: 0,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Manage Properties">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Settings />}
            onClick={() => openPropertiesDrawer(params.row)}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            Props
          </Button>
        </Tooltip>
      ),
    },
  ];

  const formFields = [
    {
      name: 'headerCode',
      label: 'Header Code',
      required: true,
      validate: (value) => {
        if (!value) return 'Header Code is required';
        if (value.length > 100) return 'Header Code must be 100 characters or less';
        return null;
      },
    },
    {
      name: 'category',
      label: 'Category',
      required: false,
      validate: (value) => {
        if (value && value.length > 100) return 'Category must be 100 characters or less';
        return null;
      },
    },
    {
      name: 'subCategory',
      label: 'Sub Category',
      required: false,
      validate: (value) => {
        if (value && value.length > 100) return 'Sub Category must be 100 characters or less';
        return null;
      },
    },
    ...transformationHeaderFormFieldsForFixedType(fixedType),
    {
      name: 'targetJson',
      label: 'Target JSON',
      required: false,
      multiline: true,
      rows: 6,
      validate: (value) => {
        if (value) {
          try {
            JSON.parse(value);
          } catch (e) {
            return 'Target JSON must be valid JSON format';
          }
        }
        return null;
      },
    },
  ];

  const initialFormData = useMemo(
    () => ({
      id: '',
      headerCode: '',
      category: '',
      subCategory: '',
      targetJson: '',
      ...transformationHeaderExtendedInitialData,
      type: fixedType,
    }),
    [fixedType]
  );

  const prepareEditData = (row) => ({
    ...prepareTransformationHeaderFormRow(row),
    type: fixedType,
  });

  return (
    <>
      <DynamicContainer
        title={title}
        icon={Transform}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="id"
        rows={filteredHeaders}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        prepareEditData={prepareEditData}
        isLoading={isLoading}
        functionCode={functionCode}
      />

      <TransformationPropertiesDrawer
        open={isPropertiesDrawerOpen}
        onClose={() => setIsPropertiesDrawerOpen(false)}
        headerCode={selectedHeader?.headerCode}
        headerName={selectedHeader?.headerCode}
        title={propertiesTitle}
      />
    </>
  );
};

export default CDXTransformation;
